class CollectionAttributeSidebar {
  constructor(collectionTags) {
    this._collectionTagsGlobal = collectionTags; // Tags for the current collection, defined in the collection-attribute-sidebar.liquid snippet
    this._collectionTags = [];
    this._filterCollectionTags = []; // Tags for the current collection, if fitlers are applied
    this._filterData = {};
    this._priceData = [];
    this._appliedFilters = [];
    this._productsToRemove = [];
    this._currentSortBy = {
      optionValue: "default",
      dataSort: "default",
    };
    this._currentPaginationState = {
      paginationIsVisible: true,
      paginationWrapper: null,
    };
    this._hasFitment = false;
    this._currentFitment = null;
    this._isTier2Selected = false;
    this._tagsContainer = document.getElementById("tags");

    // Debouncing and request cancellation
    this._filterDebounceTimer = null;
    this._filterDebounceDelay = 150; // ms
    this._currentAbortController = null;

    // Cached DOM references
    this._productGrid = document.getElementById("main-collection-product-grid");
    this._collectionContainer = document.getElementById("collection-container");
    this._loadingOverlay = document.querySelector(".loading-overlay--custom");
    this._paginationWrapper = document.querySelector(".pagination-wrapper");
    this._clearAllBtn = document.getElementById("clear-all-filters");
    this._activeFiltersContainer = document.getElementById("active-filters");
    this._emptyMessage = document.getElementById("collection-empty-message");
    this._noProductsMessage = document.getElementById("collection-no-products-message");
    this._appliedFilterTagsContainer = document.getElementById("applied-filter-tags");

    // In-memory caches for GraphQL responses
    this._fitmentCache = new Map(); // SKU -> { metafield, tags }
    this._tagCache = new Map(); // cacheKey -> { data, timestamp }
    this._tagCacheTTL = 5 * 60 * 1000; // 5 minutes TTL

    this.initData(this._collectionTagsGlobal);
    this.bindEvents();
    this.loadFiltersFromSession();

    // If fitment is active and loadFiltersFromSession didn't trigger applyFilters,
    // rebuild facets from the DOM so they match the fitment-filtered products on the page
    if (this._hasFitment && this._appliedFilters.length === 0) {
      this.handleCollectionTagsUpdate();
    }
  }

  // Getters and setters
  get classState() {
    return {
      collectionTags: this._collectionTags,
      filterData: this._filterData,
      priceData: this._priceData,
      appliedFilters: this._appliedFilters,
      currentSortBy: this._currentSortBy,
      hasFitment: this._hasFitment,
      currentFitment: this._currentFitment,
      isTier2Selected: this._isTier2Selected,
    };
  }

  getState() {
    return this.classState;
  }

  static openFilterSidebar() {
    document.getElementById("collection-attribute-sidebar").style.width =
      "350px";
  }

  openSidebar() {
    document.getElementById("collection-attribute-sidebar").style.width =
      "350px";
  }

  closeSidebar() {
    const sidebar = document.getElementById("collection-attribute-sidebar");
    sidebar.style.width = "0";
    if (sidebar.classList.contains("filter-sidebar-open")) {
      sidebar.classList.remove("filter-sidebar-open");
    }
  }

  async initData(collectionTagsArg) {
    const rawFitmentData = localStorage.getItem("fitmentData");
    let parsedFitmentData = rawFitmentData ? JSON.parse(rawFitmentData) : null;

    const threeDaysInMs = 1000 * 60 * 60 * 24 * 3;
    const lastUpdatedMs = parsedFitmentData?.lastUpdated
      ? Number(parsedFitmentData.lastUpdated)
      : null;

    const isExpired =
      lastUpdatedMs &&
      Number.isFinite(lastUpdatedMs) &&
      Date.now() - lastUpdatedMs > threeDaysInMs;

    if (isExpired) {
      localStorage.removeItem("fitmentData");
      parsedFitmentData = null;
    }

    const hasNonEmptyVehicle =
      parsedFitmentData &&
      Object.keys(parsedFitmentData.currentVehicle || {}).length > 0;

    this._hasFitment = Boolean(hasNonEmptyVehicle);
    this._currentFitment = this._hasFitment
      ? parsedFitmentData.currentVehicle
      : null;
    this._collectionTags = collectionTagsArg;
    const tags = this._collectionTags;
    const tagsContainer = this._tagsContainer;

    // Create Category section first
    const categorySection = this.createCollapsibleSection("Category", true);
    const categorySectionContent =
      categorySection.querySelector("#category-content");
    tagsContainer.appendChild(categorySection);

    this.processTags(tags, categorySectionContent, tagsContainer);
  }

  processTags(tags, categorySectionContent, tagsContainer) {
    // Build a quick lookup: { 'Color': Set(['Black']), 'Size': Set(['M']) }
    const selectedByType = (this._appliedFilters || []).reduce((acc, tag) => {
      const [rawType, value] = tag.split(":");
      const type = this.toTitleCase(rawType);
      if (!acc[type]) acc[type] = new Set();
      acc[type].add(value);
      return acc;
    }, {});

    // Process Tier2 tags for the Category section
    let tier2Tags = tags.filter((tag) => tag.toLowerCase().startsWith("tier2:"));
    const selectedTier2 = selectedByType["Tier2"];

    if (selectedTier2 && selectedTier2.size > 0) {
      // compare trimmed values to avoid whitespace mismatches
      tier2Tags = tier2Tags.filter((tag) => selectedTier2.has(tag.split(":")[1].trim()));
    }

    tier2Tags.forEach((tag) => {
      const [, rawValue] = tag.split(":");
      const filterValue = rawValue.trim();
      // Only skip "Lift Kits" when no Tier2 selection is active
      if ((!selectedTier2 || selectedTier2.size === 0) && filterValue.toLowerCase() === "lift kits") return;
      this.createFilterElements(
        categorySectionContent,
        "Tier2",
        filterValue,
        "tier2"
      );
    });

    let tier3Tags = tags.filter((tag) => tag.toLowerCase().startsWith("tier3:"));
    const selectedTier3 = selectedByType["Tier3"];

    if (selectedTier3 && selectedTier3.size > 0) {
      tier3Tags = tier3Tags.filter((tag) => selectedTier3.has(tag.split(":")[1]));
    }

    tier3Tags.forEach((tag) => {
      const [, filterValue] = tag.split(":");
      this.createFilterElements(
        categorySectionContent,
        "Tier3",
        filterValue,
        "tier3"
      );
    });

    // Process other tags
    tags.forEach((tag) => {
      if (
        tag.includes(":") &&
        !tag.toLowerCase().startsWith("tier2:") &&
        !tag.toLowerCase().startsWith("tier3:") &&
        !tag.toLowerCase().includes("price") &&
        tag.toLowerCase() !== "tier2: Lift Kits" &&
        tag.toLowerCase() !== "Lift Kits"
      ) {
        const [filterType, filterValue] = tag.split(":");
        const transformedFilterType = this.toTitleCase(filterType);

        if (!this._filterData[transformedFilterType]) {
          this._filterData[transformedFilterType] = [];
        }

        this._filterData[transformedFilterType].push(filterValue);
      } else if (tag.toLowerCase().includes("price")) {
        const [, filterValue] = tag.split(":");
        this._priceData.push(Number(filterValue));
      }
    });

    // Ensure selected values exist even if not present in current tags (keeps them visible/checked)
    Object.entries(selectedByType).forEach(([type, set]) => {
      if (!this._filterData[type]) this._filterData[type] = [];
      set.forEach((val) => {
        if (!this._filterData[type].includes(val)) {
          this._filterData[type].push(val);
        }
      });
    });

    // Create facet filter sections
    for (const [filterType, filterValues] of Object.entries(this._filterData)) {
      const filterSection = this.createCollapsibleSection(filterType);
      tagsContainer.appendChild(filterSection);

      const contentDiv = filterSection.querySelector(".filter-content");
      const selectedForType = selectedByType[filterType];

      const valuesToRender =
        selectedForType && selectedForType.size > 0
          ? filterValues.filter((v) => selectedForType.has(v))
          : filterValues;

      valuesToRender.sort().forEach((filterValue, index) => {
        this.createFilterElements(contentDiv, filterType, filterValue, index);
      });
    }

    // Sort filters alphanumerically
    const filterSections = document.querySelectorAll(".filter-content");

    function sortAlphaNumeric(a, b) {
      const aText = a.querySelector("label").innerText;
      const bText = b.querySelector("label").innerText;
      const aNum = parseFloat(aText);
      const bNum = parseFloat(bText);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      } else {
        return aText.localeCompare(bText, undefined, { numeric: true });
      }
    }

    for (const filterSection of Array.from(filterSections)) {
      const filterSectionChildren = Array.from(filterSection.children);
      filterSectionChildren.sort(sortAlphaNumeric);
      filterSectionChildren.forEach((child) => {
        filterSection.appendChild(child);
      });
    }
  }

  createFilterElements(container, filterType, filterValue, index) {
    if (filterType === "Tier2") {
      return this.createCategoryFilterElements(
        container,
        filterType,
        filterValue,
        index
      );
    }

    const filterValueDiv = document.createElement("div");

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.id = filterValue + index;
    checkbox.name = filterType;
    checkbox.value = filterValue;
    checkbox.classList.add("filter-checkbox", "collection-filters__item");

    checkbox.setAttribute("data-filter-type", filterType);
    checkbox.setAttribute("data-filter-value", filterValue);

    const label = document.createElement("label");
    label.htmlFor = checkbox.id;
    label.innerText = filterValue;
    label.classList.add("filter-checkbox-label");

    filterValueDiv.appendChild(checkbox);
    filterValueDiv.appendChild(label);

    container.appendChild(filterValueDiv);
  }

  createCategoryFilterElements(container, filterType, filterValue, index) {
    const filterValueDiv = document.createElement("div");

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.id = filterValue + index;
    checkbox.name = filterType;
    checkbox.value = filterValue;
    checkbox.classList.add("filter-checkbox", "collection-filters__item");

    checkbox.setAttribute("data-filter-type", filterType);
    checkbox.setAttribute("data-filter-value", filterValue);

    const label = document.createElement("label");
    label.htmlFor = checkbox.id;
    label.innerText = filterValue;
    label.classList.add("filter-checkbox-label");

    filterValueDiv.appendChild(checkbox);
    filterValueDiv.appendChild(label);

    container.appendChild(filterValueDiv);
  }

  createCollapsibleSection(filterType, isCategorySection = false) {
    const sectionDiv = document.createElement("div");
    sectionDiv.className = "filter-section";

    const headerDiv = document.createElement("div");
    headerDiv.className = "filter-header";
    headerDiv.innerText = isCategorySection
      ? "Category"
      : this.toTitleCase(
          filterType.toLowerCase().includes("length")
            ? filterType + " (IN)"
            : filterType
        );

    const contentDiv = document.createElement("div");
    contentDiv.className = "filter-content";

    if (isCategorySection) {
      contentDiv.id = "category-content";
    }

    sectionDiv.appendChild(headerDiv);
    sectionDiv.appendChild(contentDiv);

    headerDiv.addEventListener("click", () => {
      const contentDiv = sectionDiv.querySelector(".filter-content");
      contentDiv.classList.toggle("show");
      headerDiv.classList.toggle("active");
    });

    return sectionDiv;
  }

  async resetAndRebuildFilters(collectionTagsArg) {
    const tagsContainer = this._tagsContainer;
    this._filterData = {};
    this._priceData = [];
    tagsContainer.innerHTML = "";

    await this.initData(collectionTagsArg);

    this.bindCheckboxEvents();
    this.setAppliedFacetFiltersChecked();
  }

  bindEvents() {
    document
      .getElementById("open-sidebar")
      .addEventListener("click", () => this.openSidebar());
    document
      .getElementById("close-sidebar")
      .addEventListener("click", () => this.closeSidebar());

    window.addEventListener("click", (event) => {
      const sidebar = document.getElementById("collection-attribute-sidebar");
      const openButton = document.getElementById("open-sidebar");
      if (
        !sidebar.contains(event.target) &&
        event.target !== openButton &&
        !sidebar.classList.contains("filter-sidebar-open")
      ) {
        this.closeSidebar();
      }
    });

    this.bindCheckboxEvents();

    this._clearAllBtn.addEventListener("click", () => this.clearAllFilters());

    this.initLoadMoreIntegration();
  }

  handleFilterChange(event) {
    const filterType = event.target.name;
    const filterValue = event.target.value;

    if (event.target.checked) {
      this.addFilter(filterType, filterValue);
    } else {
      this.removeFilter(filterType, filterValue);
    }

    if (this._appliedFilters.length > 0) {
      this._clearAllBtn.style.display = "block";
    } else {
      this._clearAllBtn.style.display = "none";
    }

    this.saveFiltersToSession();
    this.applyFilters();
  }

  addFilter(filterType, filterValue) {
    if (filterType.includes("Tier2")) this.handleTier2Selection();

    let currentFilterTag = "";
    const currentTagLowerCase =
      filterType.toLowerCase() + ":" + filterValue.toLowerCase();
    for (let i = 0; i < this._collectionTags.length; i++) {
      if (
        this.handleizeTag(this._collectionTags[i].toLowerCase()) ===
        this.handleizeTag(currentTagLowerCase)
      ) {
        currentFilterTag = this._collectionTags[i];
        break;
      }
    }
    if (!this._appliedFilters.includes(currentFilterTag)) {
      this._appliedFilters.push(currentFilterTag);
    }
  }

  removeFilter(fitlerType, filterValue) {
    let currentFilterTag = "";
    const currentTagLowerCase =
      fitlerType.toLowerCase() + ":" + filterValue.toLowerCase();
    for (let i = 0; i < this._collectionTags.length; i++) {
      if (
        this.handleizeTag(this._collectionTags[i].toLowerCase()) ===
        this.handleizeTag(currentTagLowerCase)
      ) {
        currentFilterTag = this._collectionTags[i];
        break;
      }
    }
    if (this._appliedFilters.includes(currentFilterTag)) {
      const index = this._appliedFilters.indexOf(currentFilterTag);
      this._appliedFilters.splice(index, 1);
    }
  }

  async handleTier2Selection() {
    this._isTier2Selected = true;
  }

  clearAllFilters() {
    this._appliedFilters = [];
    document.querySelectorAll(".filter-checkbox").forEach((checkbox) => {
      checkbox.checked = false;
    });
    this._clearAllBtn.style.display = "none";
    this.saveFiltersToSession();
    this.applyFilters();
  }

  /**
   * Debounced filter application - prevents rapid successive API calls
   * when user clicks multiple filters quickly
   */
  applyFilters() {
    if (this._filterDebounceTimer) {
      clearTimeout(this._filterDebounceTimer);
    }

    if (this._currentAbortController) {
      this._currentAbortController.abort();
    }

    this._filterDebounceTimer = setTimeout(() => {
      this._applyFiltersInternal();
    }, this._filterDebounceDelay);
  }

  /**
   * Internal filter application - called after debounce delay
   */
  async _applyFiltersInternal() {
    this._currentAbortController = new AbortController();
    this.toggleLoading(true);
    const t0 = performance.now();

    try {
      const newProductGrid = await this.getFilteredProducts();

      if (this._currentAbortController.signal.aborted) return;

      this.renderProductList(newProductGrid);
      this.renderAppliedFilterTags();
      this.generateFilterList();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error applying filters:", error);
      }
    } finally {
      this.toggleLoading(false);
      console.log(`[PLP] Total load time: ${Math.round(performance.now() - t0)}ms`);

      // Rebuild filter facets asynchronously — decoupled from PLP load
      // The filter sidebar has its own loading spinner via toggleFilterLoading()
      const tTags = performance.now();
      this.handleCollectionTagsUpdate().then(() => {
        console.log(`[PLP] Filter facets updated: ${Math.round(performance.now() - tTags)}ms (GraphQL)`);
      });
    }
  }

  generateFilterList() {
    const filterContainer = this._activeFiltersContainer;
    filterContainer.innerHTML = '';

    for (let i = 0; i < this._appliedFilters.length; i++) {
      const div = document.createElement('div');
      div.classList.add('filter-tag');

      const span = document.createElement('span');
      span.classList.add('filter-label');
      span.textContent = this._appliedFilters[i].replace(/tier\d+: /g, '');

      const button = document.createElement('button');
      button.classList.add('remove-filter');
      button.innerHTML = '✕';
      button.addEventListener("click", () => {
        const [filterType, filterValue] = this._appliedFilters[i].split(":");
        this.removeFilter(filterType, filterValue);
        this.saveFiltersToSession();
        this.applyFilters();
      });

      div.appendChild(span);
      div.appendChild(button);

      filterContainer.appendChild(div);
    }
  }

  async processProductData(collectionHandle) {
    const hasFilters = this._appliedFilters.length > 0 || this._hasFitment;
    const isDefaultSort = this._currentSortBy.optionValue === "default";

    let concatenatedFilters = null;
    if (hasFilters) {
      concatenatedFilters = this._appliedFilters
        .map((filter) => this.handleizeTag(filter))
        .join("+");
    }

    try {
      if (hasFilters && isDefaultSort) {
        const products = await this.fetchProductData(
          collectionHandle,
          concatenatedFilters,
          null
        );
        return sortBy(products, this._currentSortBy);
      } else if (hasFilters && !isDefaultSort) {
        const products = await this.fetchProductData(
          collectionHandle,
          concatenatedFilters,
          "title-ascending"
        );
        return products;
      } else if (!hasFilters) {
        const products = await this.fetchUnfilteredSortedProductData(
          collectionHandle,
          "title-ascending"
        );
        return products;
      } else {
        console.warn("Unhandled case in processProductData");
      }
    } catch (error) {
      console.error(error);
    }
  }

  async fetchProductData(collectionHandle, concatenatedFilters, sortKey) {
    const url = sortKey
      ? `/collections/${collectionHandle}/${concatenatedFilters}?sort_by=title-ascending`
      : `/collections/${collectionHandle}/${await this.addConcatenatedUrlFitmentFilters(
          this._hasFitment,
          concatenatedFilters
        )}?sort_by=title-ascending`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error("Fetch failed", response.status);
        return;
      }
      const data = await response.text();
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = data;

      const newPaginationWrapper = tempDiv.querySelector(".pagination-wrapper");
      await this.handlePagination(newPaginationWrapper);

      return Array.from(
        tempDiv.querySelector("#main-collection-product-grid").children
      );
    } catch (error) {
      console.error(error);
    }
  }

  async fetchUnfilteredSortedProductData(collectionHandle, sortKey) {
    const url = !this._hasFitment
      ? `/collections/${collectionHandle}?sort_by=${sortKey}`
      : `/collections/${collectionHandle}/${await this.addConcatenatedUrlFitmentFilters(
          this._hasFitment,
          null
        )}?sort_by=${sortKey}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error("Fetch failed", response.status);
        return;
      }
      const data = await response.text();
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = data;

      const newPaginationWrapper = tempDiv.querySelector(".pagination-wrapper");
      await this.handlePagination(newPaginationWrapper);

      return Array.from(
        tempDiv.querySelector("#main-collection-product-grid").children
      );
    } catch (error) {
      console.error(error);
    }
  }

  async addConcatenatedUrlFitmentFilters(hasFitment, concatenatedFilters) {
    const fitment = await JSON.parse(localStorage.getItem("fitmentData"))
      .currentVehicle;

    let handleized;
    let modifiedUrl = "";

    if (hasFitment) {
      handleized = Object.keys(fitment).reduce((acc, key) => {
        if (key !== "submodel") {
          acc[key] = fitment[key] ? this.handleizeTag(fitment[key]) : null;
        }
        return acc;
      }, {});

      const fitmentValues = Object.values(handleized).filter(Boolean).join("+");

      modifiedUrl = concatenatedFilters
        ? `${concatenatedFilters}+${fitmentValues}`
        : fitmentValues;
    } else {
      modifiedUrl = concatenatedFilters || "";
    }

    return hasFitment ? modifiedUrl : concatenatedFilters;
  }

  async handlePagination(newPaginationWrapper) {
    this._currentPaginationState.paginationWrapper = newPaginationWrapper;
    this._currentPaginationState.paginationIsVisible =
      Boolean(newPaginationWrapper);
  }

  async getFilteredProducts() {
    this.toggleLoading(true);
    try {
      const collectionHandle = window.location.pathname.split("/")[2];
      const tFetch = performance.now();
      const sortedProducts = await this.processProductData(collectionHandle);
      console.log(`[PLP] Fetch + sort: ${Math.round(performance.now() - tFetch)}ms`);

      const productUl = this._productGrid.cloneNode(false);
      sortedProducts.forEach((product) => {
        productUl.appendChild(product);
      });

      const newProductGrid = document.createElement("div");
      newProductGrid.appendChild(productUl);

      if (this._hasFitment) {
        const tFitment = performance.now();
        this.applyFitmentToProductData(newProductGrid);
        console.log(`[PLP] Fitment match: ${Math.round(performance.now() - tFitment)}ms`);
      }

      // NOTE: handleCollectionTagsUpdate() is NOT called here — fired in _applyFiltersInternal() instead
      this.toggleLoading(false);
      return newProductGrid;
    } catch (error) {
      console.error("Error getting filtered products: ", error);
    }
  }

  renderProductList(productHtml) {
    const container = this._collectionContainer;
    const productList = this._productGrid;
    const emptyMessage = this._emptyMessage;
    const noProductsMessage = this._noProductsMessage;
    const totalProducts = parseInt(container.dataset.totalProducts, 10);

    productList.innerHTML = "";

    const filteredProducts = productHtml.querySelectorAll(
      ".product-masonry-item"
    );

    // Append products directly — already sorted by processProductData/sortBy
    filteredProducts.forEach((product) => {
      productList.appendChild(product.cloneNode(true));
    });

    // Remove non-fitting products
    if (this._productsToRemove.length > 0) {
      this._productsToRemove.forEach((sku) => {
        this.removeNonFittingProduct(sku);
      });
      this._productsToRemove = [];
    }

    const visibleProducts = productList.querySelectorAll(
      ".product-masonry-item"
    ).length;

    if (visibleProducts === 0) {
      productList.style.display = "none";
      if (totalProducts > 0) {
        emptyMessage.style.display = "block";
        noProductsMessage.style.display = "none";
      } else {
        emptyMessage.style.display = "none";
        noProductsMessage.style.display = "block";
      }
    } else {
      productList.style.display = "block";
      emptyMessage.style.display = "none";
      noProductsMessage.style.display = "none";
    }

    // Handle pagination
    const paginationWrapper = this._paginationWrapper;
    if (paginationWrapper) {
      if (
        visibleProducts > 0 &&
        this._currentPaginationState.paginationIsVisible
      ) {
        paginationWrapper.style.display = "block";
        if (this._currentPaginationState.paginationWrapper) {
          paginationWrapper.innerHTML =
            this._currentPaginationState.paginationWrapper.innerHTML;
        }
      } else {
        paginationWrapper.style.display = "none";
      }
    }

    // Resize grid items
    this.resizeAllGridItems();

    // Update product count
    const productCountElement = document.querySelector(
      ".toolbar__product-count"
    );
    if (productCountElement) {
      productCountElement.textContent = `${visibleProducts} products`;
    }
  }

  /**
   * Batched layout resize - prevents layout thrashing by separating reads and writes
   */
  resizeAllGridItems() {
    const grid = document.getElementsByClassName("halo-row--masonry")[0];
    if (!grid) return;

    const allItems = Array.from(document.getElementsByClassName("product-masonry-item"));
    if (allItems.length === 0) return;

    const rowHeight = parseInt(
      window.getComputedStyle(grid).getPropertyValue("grid-auto-rows")
    );
    const rowGap = parseInt(
      window.getComputedStyle(grid).getPropertyValue("grid-row-gap")
    );

    // Read phase: gather all measurements at once
    const measurements = allItems.map(item => {
      const productItem = item.querySelector(".product-item");
      return productItem ? productItem.getBoundingClientRect().height : 0;
    });

    // Write phase: apply all changes in a single animation frame
    requestAnimationFrame(() => {
      allItems.forEach((item, index) => {
        const height = measurements[index];
        if (height > 0) {
          const rowSpan = Math.ceil((height + rowGap) / (rowHeight + rowGap));
          item.style.gridRowEnd = "span " + rowSpan;
        }
      });

      grid.classList.add("is-show");
    });
  }

  renderAppliedFilterTags() {
    const tagsContainer = this._appliedFilterTagsContainer;
    tagsContainer.innerHTML = "";

    this._appliedFilters.forEach((filter) => {
      const filterValue = filter.split(":")[1];

      const tagElement = document.createElement("div");
      tagElement.className = "applied-filter-tag";
      tagElement.textContent = filterValue;

      const removeButton = document.createElement("button");
      removeButton.textContent = "x";
      removeButton.className = "remove-filter-button";

      removeButton.addEventListener("click", () => {
        const [filterType, filterValue] = filter.split(":");

        this.removeFilter(filterType, filterValue);

        const checkboxSelector = `.filter-checkbox[name='${filterType}'][value='${filterValue}']`;
        const correspondingCheckbox = document.querySelector(checkboxSelector);

        if (correspondingCheckbox) {
          correspondingCheckbox.checked = false;
        }
        this.saveFiltersToSession();
        this.applyFilters();
      });

      tagElement.appendChild(removeButton);
      tagsContainer.appendChild(tagElement);
    });

    if (this._appliedFilters.length > 0) {
      this._clearAllBtn.style.display = "block";
    } else {
      this._clearAllBtn.style.display = "none";
    }
  }

  async handleCollectionTagsUpdate() {
    this.toggleFilterLoading(true);
    const newTags =
      !this._hasFitment && this._appliedFilters.length === 0
        ? await this.getAllCollectionTags()
        : await this.getAllFilteredTags();
    this.updateCollectionTags(newTags);
  }

  async getAllCollectionTags() {
    const collectionHandle = window.location.pathname.split("/").pop();
    const cacheKey = `all-tags-${collectionHandle}`;

    const cached = this._tagCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this._tagCacheTTL) {
      return cached.data;
    }

    try {
      let tagsFlat = [];
      let hasNextPage = true;
      let lastCursor = null;

      while (hasNextPage) {
        let afterCursor = lastCursor ? `, after: "${lastCursor}"` : "";
        let collectionQuery = `
          query {
            collectionByHandle(handle: "${collectionHandle}") {
              products(first: 250${afterCursor}) {
                pageInfo {
                  hasNextPage
                }
                edges {
                  cursor
                  node {
                    tags
                  }
                }
              }
            }
          }`;

        const response = await makeGraphQLRequest(collectionQuery);

        if (!response || !response.data) {
          throw new Error("Invalid response from the server");
        }

        hasNextPage =
          response.data.collectionByHandle.products.pageInfo.hasNextPage;
        const edges = response.data.collectionByHandle.products.edges;

        if (edges.length > 0) {
          lastCursor = edges[edges.length - 1].cursor;

          const tags = edges.map((edge) => edge.node.tags);
          tagsFlat = tagsFlat.concat(tags.flat());
        } else {
          hasNextPage = false;
        }
      }

      this._tagCache.set(cacheKey, { data: tagsFlat, timestamp: Date.now() });
      return tagsFlat;
    } catch (error) {
      console.error("Error getting collection tags: ", error);
      return [];
    }
  }

  /**
   * Get all tags for products in the current collection, respecting applied filters and fitment.
   * Handles pagination for collections with 250+ products.
   * Results are cached with TTL to avoid redundant API calls.
   */
  async getAllFilteredTags() {
    const collectionHandle = window.location.pathname.split("/").pop();

    // Build filters array from applied filters using Shopify's filter syntax
    const filterObjs = this._appliedFilters.map(f =>
      `{tag: "${f.replace(/"/g, '\\"')}"}`
    );

    const hasFitment = this._hasFitment &&
      this._currentFitment &&
      Object.keys(this._currentFitment).length > 0;

    // Include fitment year/make/model as tag filters so the GraphQL query
    // returns the same product set as the URL-based product grid filtering
    if (hasFitment) {
      const fitment = this._currentFitment;
      if (fitment.year) filterObjs.push(`{tag: "${fitment.year}"}`);
      if (fitment.make) filterObjs.push(`{tag: "${fitment.make.replace(/"/g, '\\"')}"}`);
      if (fitment.model) filterObjs.push(`{tag: "${fitment.model.replace(/"/g, '\\"')}"}`);
    }

    const filtersArg = filterObjs.length
      ? `, filters: [${filterObjs.join(",")}]`
      : "";

    // Build cache key from filter state and fitment
    const fitmentKey = hasFitment ? JSON.stringify(this._currentFitment) : 'none';
    const filtersKey = this._appliedFilters.sort().join('|');
    const cacheKey = `filtered-tags-${collectionHandle}-${filtersKey}-${fitmentKey}`;

    // Check cache first (with TTL)
    const cached = this._tagCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this._tagCacheTTL) {
      return cached.data;
    }

    let allTags = [];
    let hasNextPage = true;
    let cursor = null;

    // Paginate through all products in collection
    while (hasNextPage) {
      const afterArg = cursor ? `, after: "${cursor}"` : "";

      // Use different query structure based on whether fitment checking is needed
      // When fitment is active, fetch ALL variants' fitment metafields (not just first)
      // so we can check if any variant matches the selected fitment
      const query = hasFitment
        ? `query {
            collection(handle: "${collectionHandle}") {
              products(first: 250${filtersArg}${afterArg}) {
                pageInfo { hasNextPage }
                edges {
                  cursor
                  node {
                    tags
                    variants(first: 100) {
                      edges {
                        node {
                          sku
                          metafield(namespace: "pim", key: "fitment_details") {
                            value
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }`
        : `query {
            collection(handle: "${collectionHandle}") {
              products(first: 250${filtersArg}${afterArg}) {
                pageInfo { hasNextPage }
                edges {
                  cursor
                  node { tags }
                }
              }
            }
          }`;

      const resp = await makeGraphQLRequest(query);

      // Guard against missing data
      if (!resp?.data?.collection?.products) {
        console.warn("getAllFilteredTags: No collection data returned");
        break;
      }

      const { pageInfo, edges } = resp.data.collection.products;
      hasNextPage = pageInfo.hasNextPage;

      if (edges.length > 0) {
        cursor = edges[edges.length - 1].cursor;

        for (const edge of edges) {
          if (hasFitment) {
            // Check fitment at the variant level — a product's tags are included
            // only if at least one variant matches the selected fitment.
            // Products with no fitment metafield on any variant = universal fit (included).
            const variantEdges = edge.node.variants?.edges || [];
            let hasAnyFitmentData = false;
            let hasMatchingVariant = false;

            for (const variantEdge of variantEdges) {
              const mf = variantEdge.node?.metafield;
              if (mf && mf.value) {
                hasAnyFitmentData = true;
                if (isSubmodelMatch(mf.value, this._currentFitment)) {
                  hasMatchingVariant = true;
                  break; // one matching variant is enough
                }
              }
            }

            // Include tags if: no variants have fitment data (universal fit)
            // OR at least one variant matches the selected fitment
            if (!hasAnyFitmentData || hasMatchingVariant) {
              allTags.push(...edge.node.tags);
            }
          } else {
            // No fitment to check, include all tags
            allTags.push(...edge.node.tags);
          }
        }
      } else {
        hasNextPage = false;
      }
    }

    // Dedupe and cache the result
    const uniqueTags = [...new Set(allTags)];
    this._tagCache.set(cacheKey, { data: uniqueTags, timestamp: Date.now() });

    return uniqueTags;
  }

  // buildTagFilterQuery - REMOVED
  // Replaced by inline filter building in getAllFilteredTags() using Shopify's collection filter syntax

  async updateCollectionTags(newTags) {
    try {
      let tags = [];
      newTags.forEach((tag) => {
        if (!tags.includes(tag)) {
          tags.push(tag);
        }
      });

      this._collectionTags = tags;
      await this.resetAndRebuildFilters(this._collectionTags);
      this.toggleFilterLoading(false);
    } catch (error) {
      console.error("Error updating collection tags: ", error);
    }
  }

  /**
   * Fitment matching — reads data-fitment-detail attribute from DOM instead of making API calls.
   * Synchronous, zero network requests.
   */
  applyFitmentToProductData(productDataHtml) {
    const currentFitment = this._currentFitment;
    if (!currentFitment || Object.keys(currentFitment).length === 0) return;

    const elements = productDataHtml.querySelectorAll(
      "#main-collection-product-grid li > div"
    );
    if (elements.length === 0) return;

    for (const element of elements) {
      const fitmentDetail = element.getAttribute("data-fitment-detail");
      const variantObj = parseVariantData(element);
      const sku = variantObj?.variantSku;
      if (!sku) continue;

      // Empty/missing = universal fit (always included)
      if (fitmentDetail && fitmentDetail.trim().length > 0) {
        const matched = isSubmodelMatch(fitmentDetail, currentFitment);
        if (matched === false) {
          this._productsToRemove.push(sku);
        }
      }
    }
  }

  // handleProductMetafieldAndTags - REMOVED
  // filterProductByVariantSku - REMOVED
  // Logic now integrated into applyFitmentToProductData() DOM-based approach

  removeNonFittingProduct(sku) {
    const elements = document.querySelectorAll(
      "#main-collection-product-grid li > div"
    );
    elements.forEach((element) => {
      const variantObjSku = parseVariantData(element).variantSku;
      if (variantObjSku === sku) {
        element.parentElement.remove();
      }
    });
    return;
  }

  // ─── Load More Integration ────────────────────────────────────────────
  // Overrides theme.js's halo.doAjaxInfiniteScrollingGetContent so that
  // all load-more requests (click and scroll triggers) route through our
  // pipeline: fitment filtering → price sort → facet rebuild → masonry.
  // ──────────────────────────────────────────────────────────────────────

  /**
   * Override halo.doAjaxInfiniteScrollingGetContent at runtime.
   * Both theme.js triggers (scroll at line 6857, click at line 6870)
   * call this single method, so one override covers everything.
   */
  initLoadMoreIntegration() {
    const self = this;

    // Intercept [data-infinite-scrolling] clicks in the CAPTURING phase,
    // which fires before jQuery's delegated handler in theme.js.
    // This prevents theme.js from also fetching/appending products.
    document.addEventListener(
      "click",
      function (event) {
        const link = event.target.closest("[data-infinite-scrolling]");
        if (!link) return;

        // Block theme.js's jQuery handler and default navigation
        event.preventDefault();
        event.stopImmediatePropagation();

        const url = link.getAttribute("href");
        console.log("[LoadMore] Click intercepted — URL:", url);

        if (url && url !== "javascript:void(0)") {
          self.loadMoreProducts(url, link);
        }
      },
      true // capturing phase
    );

    // Also neutralize the scroll-based auto-trigger from theme.js.
    // theme.js binds: $win.on('scroll load', ...) which calls
    // halo.doAjaxInfiniteScrollingGetContent when scroll position
    // nears the pagination. We intercept by adding our own scroll
    // handler that adds is-loading class to prevent theme.js from firing,
    // then runs our logic instead.
    let scrollDebounce = null;
    window.addEventListener("scroll", () => {
      const pageInfinite = document.querySelector(".pagination-infinite");
      if (!pageInfinite) return;

      const link = pageInfinite.querySelector("[data-infinite-scrolling]");
      if (!link || link.classList.contains("is-loading")) return;

      const position = pageInfinite.getBoundingClientRect().top + window.scrollY - 500;
      if (window.scrollY > position) {
        const url = link.getAttribute("href");
        if (!url || url === "javascript:void(0)") return;

        // Add is-loading immediately to block theme.js scroll handler
        link.classList.add("is-loading");

        // Debounce to avoid rapid-fire
        if (scrollDebounce) clearTimeout(scrollDebounce);
        scrollDebounce = setTimeout(() => {
          console.log("[LoadMore] Scroll triggered — URL:", url);
          self.loadMoreProducts(url, link);
        }, 100);
      }
    });

    console.log("[LoadMore] Click + scroll interceptors installed");
  }

  /**
   * Fetch the next page of products, apply fitment filtering (in-memory),
   * merge with existing products, sort by price, rebuild facets, and
   * recalculate masonry layout.
   */
  async loadMoreProducts(url, linkElement) {
    if (!url || !linkElement) return;

    const loadMoreText = linkElement.getAttribute("data-load-more") || linkElement.textContent;
    const loadingText = linkElement.getAttribute("data-loading-more") || "Loading...";
    linkElement.textContent = loadingText;
    linkElement.classList.add("is-loading");

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
      const html = await response.text();

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;

      const newGrid = tempDiv.querySelector("#CollectionProductGrid");
      if (!newGrid) {
        console.warn("[LoadMore] No #CollectionProductGrid in response");
        return;
      }

      const newProductList = newGrid.querySelector(".productListing");
      const newProductItems = newProductList
        ? Array.from(newProductList.children).filter((el) =>
            el.classList.contains("product")
          )
        : [];
      const newPagination = newGrid.querySelector(".pagination");

      const fittedNewProducts = this.applyFitmentToNewProducts(newProductItems);

      const existingProducts = Array.from(
        this._productGrid.querySelectorAll(":scope > .product")
      );

      const allProducts = [...existingProducts, ...fittedNewProducts];
      const sorted = sortBy(allProducts, this._currentSortBy);

      this._productGrid.innerHTML = "";
      sorted.forEach((product) => this._productGrid.appendChild(product));

      const currentPagination = document.querySelector(
        "#CollectionProductGrid .pagination"
      );
      if (currentPagination && newPagination) {
        currentPagination.replaceWith(newPagination);
      } else if (currentPagination && !newPagination) {
        currentPagination.closest(".pagination-wrapper")?.remove();
      }

      this.handleCollectionTagsUpdate();
      this.resizeAllGridItems();
      this.runThemePostProcessing();

      const countEl = document.querySelector(".toolbar__product-count");
      if (countEl) {
        const visible = this._productGrid.querySelectorAll(
          ".product-masonry-item"
        ).length;
        countEl.textContent = `${visible} products`;
      }

      console.log(
        `[LoadMore] ${fittedNewProducts.length} new products added (${newProductItems.length - fittedNewProducts.length} filtered by fitment), total: ${sorted.length}`
      );
    } catch (error) {
      console.error("[LoadMore] Error:", error);
    } finally {
      linkElement.textContent = loadMoreText;
      linkElement.classList.remove("is-loading");
    }
  }

  /**
   * Filter new product elements by fitment before DOM insertion.
   * Returns only products that match the current fitment (or have no
   * fitment data, i.e. universal fit). Pure function — no side effects.
   */
  applyFitmentToNewProducts(productElements) {
    if (!this._hasFitment || !this._currentFitment) return productElements;

    return productElements.filter((li) => {
      const productDiv = li.querySelector("[data-fitment-detail]");
      if (!productDiv) return true; // no fitment attribute = universal fit

      const fitmentDetail = productDiv.getAttribute("data-fitment-detail");
      if (!fitmentDetail || fitmentDetail.trim().length === 0) return true;

      return isSubmodelMatch(fitmentDetail, this._currentFitment);
    });
  }

  /**
   * Re-initialize theme.js features after loading new products.
   * Replicates the post-processing from theme.js ajaxInfiniteScrollingMapData
   * (compare, wishlist, currency conversion).
   */
  runThemePostProcessing() {
    try {
      // Currency conversion — Currency is a global, halo is not needed
      if (
        typeof Currency !== "undefined" &&
        typeof Currency.convertAll === "function" &&
        $("#currencies .active").length > 0
      ) {
        Currency.convertAll(
          window.shop_currency,
          $("#currencies .active").attr("data-currency"),
          "span.money",
          "money_format"
        );
      }

      // SPR (Shopify Product Reviews) badge re-init
      if (
        window.SPR &&
        typeof window.SPR.registerCallbacks === "function" &&
        document.querySelectorAll(".shopify-product-reviews-badge").length > 0
      ) {
        window.SPR.registerCallbacks();
        window.SPR.initRatingHandler();
        window.SPR.initDomEls();
        window.SPR.loadProducts();
        window.SPR.loadBadges();
      }
    } catch (e) {
      console.warn("[LoadMore] Theme post-processing warning:", e);
    }
  }

  toggleLoading(show) {
    const loadingOverlay = this._loadingOverlay;
    if (loadingOverlay) {
      loadingOverlay.style.display = show ? "block" : "none";
    }
  }

  toggleFilterLoading(show) {
    const tagsDiv = document.getElementById("tags");
    if (show) {
      // Only add spinner if one doesn't already exist
      if (!tagsDiv.querySelector("#js-loading-div")) {
        const loadingDiv = `<div id="js-loading-div" class="loading-overlay loading-overlay--custom" style="display: block;">
                    <div class="loading-overlay__spinner"><svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>
                </div>`;
        tagsDiv.insertAdjacentHTML("afterbegin", loadingDiv);
      }
    } else {
      const spinner = tagsDiv.querySelector("#js-loading-div");
      if (spinner) spinner.remove();
    }
  }

  toTitleCase(str) {
    return str
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/(\b[a-z](?!\s))/g, (x) => x.toUpperCase());
  }

  handleizeTag(input) {
    return input
      .toLowerCase()
      .replace(/:/g, "-")
      .replace(/\./g, "-")
      .replace(/[^a-z0-9-\s]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  setLoadingState() {
    this._sidebar.classList.add("loading");
  }

  saveFiltersToSession() {
    const state = {
      selectedPriceRange: this._selectedPriceRange,
      appliedFilters: this._appliedFilters,
      currentSortBy: this._currentSortBy.optionValue,
    };
    sessionStorage.setItem(
      `fitlers-${window.location.pathname.split("/")[2]}`,
      JSON.stringify(state)
    );
  }

  loadFiltersFromSession() {
    const savedState = sessionStorage.getItem(
      `fitlers-${window.location.pathname.split("/")[2]}`
    );

    if (savedState) {
      const state = JSON.parse(savedState);
      this._selectedPriceRange = state.selectedPriceRange || { min: 0, max: 0 };
      this._appliedFilters = state.appliedFilters || [];
      this._currentSortBy.optionValue =
        state.currentSortBy.optionValue || "default";

      this.setAppliedFacetFiltersChecked();
      this.applyFilters();

      if (this._appliedFilters.length > 0) {
        this._clearAllBtn.style.display = "block";
      } else {
        this._clearAllBtn.style.display = "none";
      }
    } else if (!savedState) {
      this.saveFiltersToSession();
      this.applyFilters();
    }
  }

  setAppliedFacetFiltersChecked() {
    this._appliedFilters.forEach((filterTag) => {
      const [filterType, filterValue] = filterTag.split(":");
      const checkboxSelector = `.filter-checkbox[name='${this.toTitleCase(
        filterType
      )}'][value='${filterValue}']`;
      const checkboxElement = document.querySelector(checkboxSelector);
      if (checkboxElement) {
        checkboxElement.checked = true;
      }
    });
  }

  bindCheckboxEvents() {
    document.querySelectorAll(".filter-checkbox").forEach((checkbox) => {
      checkbox.addEventListener("change", (event) =>
        this.handleFilterChange(event)
      );
    });
  }
}

const collectionAttributeSidebar = new CollectionAttributeSidebar(
  collectionTags
);

// Helper functions
function sortBy(products, currentSortBy) {
  switch (currentSortBy.optionValue) {
    case "low-to-high":
      return products.sort((a, b) => {
        const priceA = parseFloat(
          a
            .querySelector(".price-item--regular")
            .textContent.replace(/[^0-9.]/g, "")
        );
        const priceB = parseFloat(
          b
            .querySelector(".price-item--regular")
            .textContent.replace(/[^0-9.]/g, "")
        );
        return priceA - priceB;
      });
    case "high-to-low":
      return products.sort((a, b) => {
        const priceA = parseFloat(
          a
            .querySelector(".price-item--regular")
            .textContent.replace(/[^0-9.]/g, "")
        );
        const priceB = parseFloat(
          b
            .querySelector(".price-item--regular")
            .textContent.replace(/[^0-9.]/g, "")
        );
        return priceB - priceA;
      });
    case "alpha-asc":
      return products.sort((a, b) => {
        const titleA = a.querySelector(".card-title").textContent.trim();
        const titleB = b.querySelector(".card-title").textContent.trim();
        return titleA.localeCompare(titleB);
      });
    case "alpha-desc":
      return products.sort((a, b) => {
        const titleA = a.querySelector(".card-title").textContent.trim();
        const titleB = b.querySelector(".card-title").textContent.trim();
        return titleB.localeCompare(titleA);
      });
    default:
      return products.sort((a, b) => {
        const priceElA = a.querySelector(".card-price");
        const priceElB = b.querySelector(".card-price");
        const priceA = priceElA
          ? parseFloat(priceElA.textContent.replace(/[^0-9.]/g, "")) || 0
          : 0;
        const priceB = priceElB
          ? parseFloat(priceElB.textContent.replace(/[^0-9.]/g, "")) || 0
          : 0;
        return priceB - priceA;
      });
  }
}

function parseVariantData(element) {
  const jsonData = element.getAttribute("data-json-product");
  const decodedStr = decodeURIComponent(jsonData);
  const parsedObj = JSON.parse(decodedStr);
  return {
    productTitle: parsedObj.variants[0].name,
    variantSku: parsedObj.variants[0].sku,
  };
}

/**
 * Makes a GraphQL request to the Shopify Storefront API
 */
async function makeGraphQLRequest(query) {
  try {
    const response = await fetch(
      "/api/2025-04/graphql.json",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": "cf6cb78c9ef033d960b53dd92195c129",
        },
        body: JSON.stringify({ query }),
      }
    );

    if (!response.ok) {
      console.error(`GraphQL request failed: ${response.status} ${response.statusText}`);
      return { data: null, errors: [{ message: `HTTP ${response.status}` }] };
    }

    const json = await response.json();

    if (json.errors && json.errors.length > 0) {
      console.error("GraphQL errors:", json.errors);
    }

    return json;
  } catch (error) {
    console.error("GraphQL request error:", error);
    return { data: null, errors: [{ message: error.message }] };
  }
}

function isSubmodelMatch(fitmentStrings, fitmentSelection) {
  const { year, make, model, submodel, drive_type, fuel_type } =
    fitmentSelection;

  function stringToObject(fitmentStrings) {
    return fitmentStrings.split(";").map((str) => {
      const [year, make, model, sub_model, drive_type, fuel_type] =
        str.split("|");
      return { year, make, model, sub_model, drive_type, fuel_type };
    });
  }

  function isMatch(fitmentValue, inputValue, wildcard = "*") {
    return (
      inputValue &&
      fitmentValue &&
      (fitmentValue.toLowerCase() === inputValue.toLowerCase() ||
        fitmentValue.toLowerCase() === wildcard)
    );
  }

  const fitmentStringArray = stringToObject(fitmentStrings);
  let isSubModelMatch = false;

  for (const fitment of fitmentStringArray) {
    const yearMatch = fitment.year === year;
    const makeMatch = isMatch(fitment.make, make);
    const modelMatch = isMatch(fitment.model, model);
    const driveTypeMatch = isMatch(fitment.drive_type, drive_type);
    const fuelTypeMatch = isMatch(fitment.fuel_type, fuel_type);
    const submodelMatch = isMatch(fitment.sub_model, submodel);

    if (
      yearMatch &&
      makeMatch &&
      modelMatch &&
      (driveTypeMatch || driveTypeMatch === undefined) &&
      (fuelTypeMatch || fuelTypeMatch === undefined) &&
      (submodelMatch || submodelMatch === undefined)
    ) {
      isSubModelMatch = true;
      break;
    }
  }

  return isSubModelMatch;
}

// Open sidebar externally
function toggleFilterSidebar() {
  const sidebar = document.getElementById("collection-attribute-sidebar");
  if (sidebar.classList.contains("filter-sidebar-open")) {
    sidebar.classList.remove("filter-sidebar-open");
  } else {
    sidebar.classList.add("filter-sidebar-open");
  }
}

// class CollectionAttributeSidebar {
//   constructor(collectionTags) {
//     this._collectionTagsGlobal = collectionTags; // Tags for the current collection, defined in the collection-attribute-sidebar.liquid snippet
//     this._collectionTags = [];
//     this._filterCollectionTags = []; // Tags for the current collection, if fitlers are applied
//     this._filterData = {};
//     this._priceData = [];
//     this._appliedFilters = [];
//     this._productsToRemove = [];
//     this._currentSortBy = {
//       optionValue: "default",
//       dataSort: "default",
//     };
//     this._currentPaginationState = {
//       paginationIsVisible: true,
//       paginationWrapper: null,
//     };
//     this._hasFitment = false;
//     this._currentFitment = null;
//     this._isTier2Selected = false;
//     this._tagsContainer = document.getElementById("tags");
//     this.initData(this._collectionTagsGlobal);
//     this.bindEvents();
//     this.loadFiltersFromSession();
//   }

//   // Getters and setters
//   get classState() {
//     return {
//       collectionTags: this._collectionTags,
//       filterData: this._filterData,
//       priceData: this._priceData,
//       appliedFilters: this._appliedFilters,
//       currentSortBy: this._currentSortBy,
//       hasFitment: this._hasFitment,
//       currentFitment: this._currentFitment,
//       isTier2Selected: this._isTier2Selected,
//     };
//   }

//   getState() {
//     return this.classState;
//   }

//   static openFilterSidebar() {
//     document.getElementById("collection-attribute-sidebar").style.width =
//       "350px";
//   }

//   openSidebar() {
//     document.getElementById("collection-attribute-sidebar").style.width =
//       "350px";
//   }

//   closeSidebar() {
//     const sidebar = document.getElementById("collection-attribute-sidebar");
//     sidebar.style.width = "0";
//     if (sidebar.classList.contains("filter-sidebar-open")) {
//       sidebar.classList.remove("filter-sidebar-open");
//     }
//   }

//   async initData(collectionTagsArg) {
//     const rawFitmentData = localStorage.getItem("fitmentData");
//     let parsedFitmentData = rawFitmentData ? JSON.parse(rawFitmentData) : null;

//     const threeDaysInMs = 1000 * 60 * 60 * 24 * 3;
//     const lastUpdatedMs = parsedFitmentData?.lastUpdated
//     ? Number(parsedFitmentData.lastUpdated)
//     : null;
 
//     const isExpired =
//     lastUpdatedMs &&
//     Number.isFinite(lastUpdatedMs) &&
//     Date.now() - lastUpdatedMs > threeDaysInMs;

//     if (isExpired) {
//     localStorage.removeItem("fitmentData");
//     parsedFitmentData = null;
//     }

//     const hasNonEmptyVehicle =
//     parsedFitmentData &&
//     Object.keys(parsedFitmentData.currentVehicle || {}).length > 0;

//     this._hasFitment = Boolean(hasNonEmptyVehicle);
//     this._currentFitment = this._hasFitment
//     ? parsedFitmentData.currentVehicle
//     : null;
//     this._collectionTags = collectionTagsArg;
//     const tags = this._collectionTags;
//     const tagsContainer = this._tagsContainer;

//     // Create Category section first
//     const categorySection = this.createCollapsibleSection("Category", true);
//     const categorySectionContent =
//       categorySection.querySelector("#category-content");
//     tagsContainer.appendChild(categorySection);

//     this.processTags(tags, categorySectionContent, tagsContainer);

//     // Check current state and log
//     console.log("Current state: ", this.getState());
//   }

//   processTags(tags, categorySectionContent, tagsContainer) {
//   // Build a quick lookup: { 'Color': Set(['Black']), 'Size': Set(['M']) }
//   const selectedByType = (this._appliedFilters || []).reduce((acc, tag) => {
//     const [rawType, value] = tag.split(":");
//     const type = this.toTitleCase(rawType);
//     if (!acc[type]) acc[type] = new Set();
//     acc[type].add(value);
//     return acc;
//   }, {});

//   // Process Tier2 tags for the Category section
//   let tier2Tags = tags.filter((tag) => tag.toLowerCase().startsWith("tier2:"));
//   const selectedTier2 = selectedByType["Tier2"];
//   if (selectedTier2 && selectedTier2.size > 0) {
//     // compare trimmed values to avoid whitespace mismatches
//     tier2Tags = tier2Tags.filter((tag) => selectedTier2.has(tag.split(":")[1].trim()));
//   }
//   tier2Tags.forEach((tag) => {
//     const [, rawValue] = tag.split(":");
//     const filterValue = rawValue.trim();

//     // Only skip "Lift Kits" when no Tier2 selection is active
//     if ((!selectedTier2 || selectedTier2.size === 0) && filterValue.toLowerCase() === "lift kits") return;

//     this.createFilterElements(
//       categorySectionContent,
//       "Tier2",
//       filterValue,
//       "tier2"
//     );
//   });

//   let tier3Tags = tags.filter((tag) => tag.toLowerCase().startsWith("tier3:"));
//   const selectedTier3 = selectedByType["Tier3"];
//   if (selectedTier3 && selectedTier3.size > 0) {
//     tier3Tags = tier3Tags.filter((tag) => selectedTier3.has(tag.split(":")[1]));
//   }
//   tier3Tags.forEach((tag) => {
//     const [, filterValue] = tag.split(":");
//     this.createFilterElements(
//       categorySectionContent,
//       "Tier3",
//       filterValue,
//       "tier3"
//     );
//   });

//   // Process other tags
//   tags.forEach((tag) => {
//     if (
//       tag.includes(":") &&
//       !tag.toLowerCase().startsWith("tier2:") &&
//       !tag.toLowerCase().startsWith("tier3:") &&
//       !tag.toLowerCase().includes("price") &&
//       tag.toLowerCase() !== "tier2: Lift Kits" &&
//       tag.toLowerCase() !== "Lift Kits"
//     ) {
//       const [filterType, filterValue] = tag.split(":");
//       const transformedFilterType = this.toTitleCase(filterType);

//       if (!this._filterData[transformedFilterType]) {
//         this._filterData[transformedFilterType] = [];
//       }

//       this._filterData[transformedFilterType].push(filterValue);
//     } else if (tag.toLowerCase().includes("price")) {
//       const [, filterValue] = tag.split(":");
//       this._priceData.push(Number(filterValue));
//     }
//   });

//   // Ensure selected values exist even if not present in current tags (keeps them visible/checked)
//   Object.entries(selectedByType).forEach(([type, set]) => {
//     if (!this._filterData[type]) this._filterData[type] = [];
//     set.forEach((val) => {
//       if (!this._filterData[type].includes(val)) {
//         this._filterData[type].push(val);
//       }
//     });
//   });

//   // Create facet filter sections
//   for (const [filterType, filterValues] of Object.entries(this._filterData)
//     .filter(([type, vals]) => vals.length > 1 || (selectedByType[type] && selectedByType[type].size > 0))) {
//     const filterSection = this.createCollapsibleSection(filterType);
//     tagsContainer.appendChild(filterSection);

//     const contentDiv = filterSection.querySelector(".filter-content");
//     const selectedForType = selectedByType[filterType];
//     const valuesToRender =
//       selectedForType && selectedForType.size > 0
//         ? filterValues.filter((v) => selectedForType.has(v))
//         : filterValues;

//     valuesToRender.sort().forEach((filterValue, index) => {
//       this.createFilterElements(contentDiv, filterType, filterValue, index);
//     });
//   }

//   // Sort filters alphanumerically
//   const filterSections = document.querySelectorAll(".filter-content");
//   function sortAlphaNumeric(a, b) {
//     const aText = a.querySelector("label").innerText;
//     const bText = b.querySelector("label").innerText;
//     const aNum = parseFloat(aText);
//     const bNum = parseFloat(bText);
//     if (!isNaN(aNum) && !isNaN(bNum)) {
//       return aNum - bNum;
//     } else {
//       return aText.localeCompare(bText, undefined, { numeric: true });
//     }
//   }
//   for (const filterSection of Array.from(filterSections)) {
//     const filterSectionChildren = Array.from(filterSection.children);
//     filterSectionChildren.sort(sortAlphaNumeric);
//     filterSectionChildren.forEach((child) => {
//       filterSection.appendChild(child);
//     });
//   }
// }


//   createFilterElements(container, filterType, filterValue, index) {
//     if (filterType === "Tier2") {
//       // Handle category-specific logic for tier2 filterType
//       return this.createCategoryFilterElements(
//         container,
//         filterType,
//         filterValue,
//         index
//       );
//     }

//     const filterValueDiv = document.createElement("div");

//     const checkbox = document.createElement("input");
//     checkbox.type = "checkbox";
//     checkbox.id = filterValue + index;
//     checkbox.name = filterType;
//     checkbox.value = filterValue;
//     checkbox.classList.add("filter-checkbox", "collection-filters__item");

//     // Adding data attributes for filterType and filterValue
//     checkbox.setAttribute("data-filter-type", filterType);
//     checkbox.setAttribute("data-filter-value", filterValue);

//     const label = document.createElement("label");
//     label.htmlFor = checkbox.id;
//     label.innerText = filterValue;
//     label.classList.add("filter-checkbox-label");

//     filterValueDiv.appendChild(checkbox);
//     filterValueDiv.appendChild(label);

//     container.appendChild(filterValueDiv);
//   }

//   createCategoryFilterElements(container, filterType, filterValue, index) {
//     // Custom logic for category filters (i.e., tier2 tags)
//     const filterValueDiv = document.createElement("div");

//     const checkbox = document.createElement("input");
//     checkbox.type = "checkbox";
//     checkbox.id = filterValue + index;
//     checkbox.name = filterType;
//     checkbox.value = filterValue;
//     checkbox.classList.add("filter-checkbox", "collection-filters__item");

//     // Adding data attributes for the Category section
//     checkbox.setAttribute("data-filter-type", filterType);
//     checkbox.setAttribute("data-filter-value", filterValue);

//     const label = document.createElement("label");
//     label.htmlFor = checkbox.id;
//     label.innerText = filterValue;
//     label.classList.add("filter-checkbox-label");

//     filterValueDiv.appendChild(checkbox);
//     filterValueDiv.appendChild(label);

//     container.appendChild(filterValueDiv);
//   }

//   createCollapsibleSection(filterType, isCategorySection = false) {
//     const sectionDiv = document.createElement("div");
//     sectionDiv.className = "filter-section";

//     const headerDiv = document.createElement("div");
//     headerDiv.className = "filter-header";
//     headerDiv.innerText = isCategorySection
//       ? "Category"
//       : this.toTitleCase(
//           filterType.toLowerCase().includes("length")
//             ? filterType + " (IN)"
//             : filterType
//         );

//     const contentDiv = document.createElement("div");
//     contentDiv.className = "filter-content";

//     if (isCategorySection) {
//       contentDiv.id = "category-content";
//     }

//     sectionDiv.appendChild(headerDiv);
//     sectionDiv.appendChild(contentDiv);

//     headerDiv.addEventListener("click", () => {
//       const contentDiv = sectionDiv.querySelector(".filter-content");
//       contentDiv.classList.toggle("show");
//       headerDiv.classList.toggle("active");
//     });

//     return sectionDiv;
//   }

//   async resetAndRebuildFilters(collectionTagsArg) {
//     // Step 1: Reset filters
//     const tagsContainer = this._tagsContainer;
//     this._filterData = {}; // Clear existing filter data
//     tagsContainer.innerHTML = ""; // Clear existing filters

//     // Step 2: Rebuild filters
//     await this.initData(collectionTagsArg);

//     // Step 3: Bind facet events
//     this.bindCheckboxEvents();
//     this.setAppliedFacetFiltersChecked();
//   }

//   bindEvents() {
//     document
//       .getElementById("open-sidebar")
//       .addEventListener("click", () => this.openSidebar());
//     document
//       .getElementById("close-sidebar")
//       .addEventListener("click", () => this.closeSidebar());
//     window.addEventListener("click", (event) => {
//       const sidebar = document.getElementById("collection-attribute-sidebar");
//       const openButton = document.getElementById("open-sidebar");

//       if (
//         !sidebar.contains(event.target) &&
//         event.target !== openButton &&
//         !sidebar.classList.contains("filter-sidebar-open")
//       ) {
//         this.closeSidebar();
//       }
//     });
//     this.bindCheckboxEvents();

//     document
//       .getElementById("clear-all-filters")
//       .addEventListener("click", () => this.clearAllFilters());
//   }

//   handleFilterChange(event) {
//     const filterType = event.target.name;
//     const filterValue = event.target.value;

//     if (event.target.checked) {
//       this.addFilter(filterType, filterValue);
//     } else {
//       this.removeFilter(filterType, filterValue);
//     }

//     if (this._appliedFilters.length > 0) {
//       document.getElementById("clear-all-filters").style.display = "block";
//     } else {
//       document.getElementById("clear-all-filters").style.display = "none";
//     }
//     this.saveFiltersToSession();
//     this.applyFilters();
//   }

//   addFilter(filterType, filterValue) {
//     if (filterType.includes("Tier2")) this.handleTier2Selection();

//     let currentFilterTag = "";
//     const currentTagLowerCase =
//       filterType.toLowerCase() + ":" + filterValue.toLowerCase();
//     for (let i = 0; i < this._collectionTags.length; i++) {
//       if (
//         this.handleizeTag(this._collectionTags[i].toLowerCase()) ===
//         this.handleizeTag(currentTagLowerCase)
//       ) {
//         currentFilterTag = this._collectionTags[i];
//         break;
//       }
//     }
//     if (!this._appliedFilters.includes(currentFilterTag)) {
//       this._appliedFilters.push(currentFilterTag);
//     }
//   }

//   removeFilter(fitlerType, filterValue) {
//     let currentFilterTag = "";
//     const currentTagLowerCase =
//       fitlerType.toLowerCase() + ":" + filterValue.toLowerCase();
//     for (let i = 0; i < this._collectionTags.length; i++) {
//       if (
//         this.handleizeTag(this._collectionTags[i].toLowerCase()) ===
//         this.handleizeTag(currentTagLowerCase)
//       ) {
//         currentFilterTag = this._collectionTags[i];
//         break;
//       }
//     }
//     if (this._appliedFilters.includes(currentFilterTag)) {
//       const index = this._appliedFilters.indexOf(currentFilterTag);
//       this._appliedFilters.splice(index, 1);
//     }
//   }

//   async handleTier2Selection() {
//     this._isTier2Selected = true;
//   }

//   clearAllFilters() {
//     this._appliedFilters = [];
//     document.querySelectorAll(".filter-checkbox").forEach((checkbox) => {
//       checkbox.checked = false;
//     });
//     document.getElementById("clear-all-filters").style.display = "none";
//     this.saveFiltersToSession();
//     this.applyFilters();
//   }

//   async applyFilters() {
//     this.toggleLoading(true);
//     const newProductGrid = await this.getFilteredProducts();
//     this.renderProductList(newProductGrid);
//     this.renderAppliedFilterTags();
//     this.generateFilterList();
//     this.toggleLoading(false);
//   }

//   generateFilterList() {
//     // Clear the existing filters
//     const filterContainer = document.getElementById('active-filters');
//     filterContainer.innerHTML = '';

//     for (let i = 0; i < this._appliedFilters.length; i++) {
//       // Create the outer div
//       const div = document.createElement('div');
//       div.classList.add('filter-tag');

//       // Create the label
//       const span = document.createElement('span');
//       span.classList.add('filter-label');
//       span.textContent = this._appliedFilters[i].replace(/tier\d+: /g, '');


//       // Create remove button
//       const button = document.createElement('button');
//       button.classList.add('remove-filter');
//       button.innerHTML = '✕';

//       button.addEventListener("click", () => {
//         const [filterType, filterValue] = this._appliedFilters[i].split(":");
//         this.removeFilter(filterType, filterValue);
//         this.saveFiltersToSession();
//         this.applyFilters();
//         this.handleAppliedFilters();
//         //console.log("Removed: ", filterValue)
//       })



//       // Append the span and button to the div
//       div.appendChild(span);
//       div.appendChild(button);

//       // Append the filter tag div to the filter container
//       filterContainer.appendChild(div);
//     }

//   }

//   async processProductData(collectionHandle) {
//     console.log("Processing product data");
//     const hasFilters = this._appliedFilters.length > 0 || this._hasFitment;
//     const isDefaultSort = this._currentSortBy.optionValue === "default";

//     let concatenatedFilters = null;
//     if (hasFilters) {
//       concatenatedFilters = this._appliedFilters
//         .map((filter) => this.handleizeTag(filter))
//         .join("+");
//     }

//     console.log("Concatenated filters:", concatenatedFilters);

//     try {
//       if (hasFilters && isDefaultSort) {
//         const products = await this.fetchProductData(
//           collectionHandle,
//           concatenatedFilters,
//           null
//         );

//         console.log(
//           "Filtered Products, first condition:",
//           collectionHandle,
//           concatenatedFilters
//         );

//         return sortBy(products, this._currentSortBy);
//       } else if (hasFilters && !isDefaultSort) {
//         const products = await this.fetchProductData(
//           collectionHandle,
//           concatenatedFilters,
//           "title-ascending"
//         );
//         console.log("Filtered Products:", products);
//         return products;
//       } else if (!hasFilters) {
//         const products = await this.fetchUnfilteredSortedProductData(
//           collectionHandle,
//           "title-ascending"
//         );
//         console.log("Unfiltered Products:", products);
//         return products;
//       } else {
//         // Optional fallback case
//         console.warn("Unhandled case in processProductData");
//       }
//     } catch (error) {
//       console.error(error);
//     }
//   }

//   async fetchProductData(collectionHandle, concatenatedFilters, sortKey) {
//     const url = sortKey
//       ? `/collections/${collectionHandle}/${concatenatedFilters}?sort_by=title-ascending`
//       : `/collections/${collectionHandle}/${await this.addConcatenatedUrlFitmentFilters(
//           this._hasFitment,
//           concatenatedFilters
//         )}?sort_by=title-ascending`;

//     console.log("URL:", url);

//     try {
//       const response = await fetch(url);
//       if (!response.ok) {
//         console.error("Fetch failed", response.status);
//         return;
//       }
//       const data = await response.text();
//       const tempDiv = document.createElement("div");
//       tempDiv.innerHTML = data;

//       const newPaginationWrapper = tempDiv.querySelector(".pagination-wrapper");
//       await this.handlePagination(newPaginationWrapper);

//       return Array.from(
//         tempDiv.querySelector("#main-collection-product-grid").children
//       );
//     } catch (error) {
//       console.error(error);
//     }
//   }

//   async fetchUnfilteredSortedProductData(collectionHandle, sortKey) {
//     const url = !this._hasFitment
//       ? `/collections/${collectionHandle}?sort_by=${sortKey}`
//       : `/collections/${collectionHandle}/${await this.addConcatenatedUrlFitmentFilters(
//           this._hasFitment,
//           null
//         )}?sort_by=${sortKey}`;

//     try {
//       const response = await fetch(url);
//       if (!response.ok) {
//         console.error("Fetch failed", response.status);
//         return;
//       }
//       const data = await response.text();
//       const tempDiv = document.createElement("div");
//       tempDiv.innerHTML = data;

//       const newPaginationWrapper = tempDiv.querySelector(".pagination-wrapper");
//       await this.handlePagination(newPaginationWrapper);

//       return Array.from(
//         tempDiv.querySelector("#main-collection-product-grid").children
//       );
//     } catch (error) {
//       console.error(error);
//     }
//   }

//   async addConcatenatedUrlFitmentFilters(hasFitment, concatenatedFilters) {
//     const fitment = await JSON.parse(localStorage.getItem("fitmentData"))
//       .currentVehicle;

//     let handleized;

//     // Concatenate the values dynamically
//     let modifiedUrl = "";

//     if (hasFitment) {
//       handleized = Object.keys(fitment).reduce((acc, key) => {
//         if (key !== "submodel") {
//           acc[key] = fitment[key] ? this.handleizeTag(fitment[key]) : null;
//         }
//         return acc;
//       }, {});

//       const fitmentValues = Object.values(handleized).filter(Boolean).join("+");

//       modifiedUrl = concatenatedFilters
//         ? `${concatenatedFilters}+${fitmentValues}`
//         : fitmentValues;
//     } else {
//       modifiedUrl = concatenatedFilters || "";
//     }

//     return hasFitment ? modifiedUrl : concatenatedFilters;
//   }

//   async handlePagination(newPaginationWrapper) {
//     // Assign the newPaginationWrapper to state
//     this._currentPaginationState.paginationWrapper = newPaginationWrapper;

//     // Update visibility state based on whether newPaginationWrapper exists
//     this._currentPaginationState.paginationIsVisible =
//       Boolean(newPaginationWrapper);
//   }

//   async getFilteredProducts() {
//     this.toggleLoading(true);
//     try {
//       const collectionHandle = window.location.pathname.split("/")[2];
//       const sortedProducts = await this.processProductData(collectionHandle);

//       const productUl = document
//         .querySelector("#main-collection-product-grid")
//         .cloneNode(false);
//       sortedProducts.forEach((product) => {
//         productUl.appendChild(product);
//       });

//       const newProductGrid = document.createElement("div");
//       newProductGrid.appendChild(productUl);

//       if (this._hasFitment) {
//         await this.applyFitmentToProductData(newProductGrid);
//       }

//       this.toggleLoading(false);

//       this.handleCollectionTagsUpdate();

//       return newProductGrid;
//     } catch (error) {
//       console.error("Error getting filtered products: ", error);
//     }
//   }

//   renderProductList(productHtml) {
//     console.log("Rendering product list");
//     const container = document.getElementById("collection-container");
//     const productList = document.getElementById("main-collection-product-grid");
//     const emptyMessage = document.getElementById("collection-empty-message");
//     const noProductsMessage = document.getElementById(
//       "collection-no-products-message"
//     );
//     const totalProducts = parseInt(container.dataset.totalProducts, 10);

//     console.log("Total products in collection:", totalProducts);

//     // Clear existing products
//     productList.innerHTML = "";

//     // Add filtered products
//     const filteredProducts = productHtml.querySelectorAll(
//       ".product-masonry-item"
//     );

//     const sortedProducts = Array.from(filteredProducts).sort((a, b) => {
//       const aPrice = a.querySelector(".price-item");
//       const bPrice = b.querySelector(".price-item");

//       if (aPrice && bPrice) {
//         const aPriceValue = parseFloat(
//           aPrice.textContent.replace(/[^0-9.-]+/g, "")
//         );
//         const bPriceValue = parseFloat(
//           bPrice.textContent.replace(/[^0-9.-]+/g, "")
//         );

//         return bPriceValue - aPriceValue;
//       }
//     });

//     sortedProducts.forEach((product) => {
//       productList.appendChild(product.cloneNode(true));
//     });

//     // Remove non-fitting products
//     if (this._productsToRemove.length > 0) {
//       console.log("Removing non-fitting products:", this._productsToRemove);
//       this._productsToRemove.forEach((sku) => {
//         this.removeNonFittingProduct(sku);
//       });
//       this._productsToRemove = [];
//     }

//     const visibleProducts = productList.querySelectorAll(
//       ".product-masonry-item"
//     ).length;

//     console.log("Visible products after filtering:", visibleProducts);

//     if (visibleProducts === 0) {
//       productList.style.display = "none";
//       if (totalProducts > 0) {
//         // Show "use fewer filters" message
//         console.log("Showing 'use fewer filters' message");
//         emptyMessage.style.display = "block";
//         noProductsMessage.style.display = "none";
//       } else {
//         // Show "no products" message
//         console.log("Showing 'no products' message");
//         emptyMessage.style.display = "none";
//         noProductsMessage.style.display = "block";
//       }
//     } else {
//       console.log("Showing product list");
//       productList.style.display = "block";
//       emptyMessage.style.display = "none";
//       noProductsMessage.style.display = "none";
//     }

//     // Handle pagination
//     const paginationWrapper = document.querySelector(".pagination-wrapper");
//     if (paginationWrapper) {
//       if (
//         visibleProducts > 0 &&
//         this._currentPaginationState.paginationIsVisible
//       ) {
//         paginationWrapper.style.display = "block";
//         if (this._currentPaginationState.paginationWrapper) {
//           paginationWrapper.innerHTML =
//             this._currentPaginationState.paginationWrapper.innerHTML;
//         }
//       } else {
//         paginationWrapper.style.display = "none";
//       }
//     }

//     // Resize grid items
//     this.resizeAllGridItems();

//     // Update product count if applicable
//     const productCountElement = document.querySelector(
//       ".toolbar__product-count"
//     );
//     if (productCountElement) {
//       productCountElement.textContent = `${visibleProducts} products`;
//     }

//     console.log("Finished rendering product list");
//   }

//   resizeAllGridItems() {
//     const allItems = document.getElementsByClassName("product-masonry-item");
//     for (let x = 0; x < allItems.length; x++) {
//       this.resizeGridItem(allItems[x]);
//     }
//     const grid = document.getElementsByClassName("halo-row--masonry")[0];
//     if (grid) {
//       grid.classList.add("is-show");
//     }
//   }

//   resizeGridItem(item) {
//     const grid = document.getElementsByClassName("halo-row--masonry")[0];
//     if (!grid) return;
//     const rowHeight = parseInt(
//       window.getComputedStyle(grid).getPropertyValue("grid-auto-rows")
//     );
//     const rowGap = parseInt(
//       window.getComputedStyle(grid).getPropertyValue("grid-row-gap")
//     );
//     const rowSpan = Math.ceil(
//       (item.querySelector(".product-item").getBoundingClientRect().height +
//         rowGap) /
//         (rowHeight + rowGap)
//     );
//     item.style.gridRowEnd = "span " + rowSpan;
//   }

//   renderAppliedFilterTags() {
//     const tagsContainer = document.getElementById("applied-filter-tags");
//     tagsContainer.innerHTML = ""; // Clear existing tags

//     // Iterate through the applied filters
//     this._appliedFilters.forEach((filter) => {
//       // Strip the filter type and colon from the filter string
//       const filterValue = filter.split(":")[1];
//       // Create a div element for each tag
//       const tagElement = document.createElement("div");
//       tagElement.className = "applied-filter-tag";
//       tagElement.textContent = filterValue;

//       // Create a remove button for the tag
//       const removeButton = document.createElement("button");
//       removeButton.textContent = "x";
//       removeButton.className = "remove-filter-button";

//       // Attach the click event to the remove button
//       removeButton.addEventListener("click", () => {
//         const [filterType, filterValue] = filter.split(":");
//         this.removeFilter(filterType, filterValue);

//         // Find the corresponding checkbox
//         const checkboxSelector = `.filter-checkbox[name='${filterType}'][value='${filterValue}']`;
//         const correspondingCheckbox = document.querySelector(checkboxSelector);

//         // Uncheck the checkbox
//         if (correspondingCheckbox) {
//           correspondingCheckbox.checked = false;
//         }
//         this.saveFiltersToSession();
//         this.applyFilters();
//       });

//       tagElement.appendChild(removeButton);
//       tagsContainer.appendChild(tagElement);
//     });

//     // Show "Clear Filters" link if there are any applied filters
//     const clearAllFiltersLink = document.getElementById("clear-all-filters");
//     if (this._appliedFilters.length > 0) {
//       clearAllFiltersLink.style.display = "block";
//     } else {
//       clearAllFiltersLink.style.display = "none";
//     }

//     console.log("Filter data: ", this._filterData);
//     console.log("Price data: ", this._priceData);

//     // Hide the entire filter component if there are no available filters to apply
//     const filterComponent = document.getElementById(
//       "collection-attribute-sidebar"
//     );
//     const openButton = document.getElementById("open-sidebar");
//     const hasAvailableFilters =
//       Object.keys(this._filterData).length > 0 || this._priceData.length > 0;
//   }
  

//   async handleCollectionTagsUpdate() {
//     this.toggleFilterLoading(true);
//     const newTags =
//       !this._hasFitment && this._appliedFilters.length === 0
//         ? await this.getAllCollectionTags()
//         : await this.getAllFilteredTags();
//     this.updateCollectionTags(newTags);
//   }

//   async getAllCollectionTags() {
//     try {
//       let tagsFlat = [];
//       let hasNextPage = true;
//       let lastCursor = null;

//       const collectionHandle = window.location.pathname.split("/").pop();

//       console.log("This is the collection handle: ", collectionHandle);

//       while (hasNextPage) {
//         let afterCursor = lastCursor ? `, after: "${lastCursor}"` : "";
//         let collectionQuery = `
//           query {
//             collectionByHandle(handle: "${collectionHandle}") {
//               products(first: 250${afterCursor}) {
//                 pageInfo {
//                   hasNextPage
//                 }
//                 edges {
//                   cursor
//                   node {
//                     tags
//                   }
//                 }
//               }
//             }
//           }`;

//           console.log("Collection query:", collectionQuery);

//         const response = await makeGraphQLRequest(collectionQuery);

//         if (!response || !response.data) {
//           throw new Error("Invalid response from the server");
//         }

//         console.log("Response:", response);

//         hasNextPage =
//           response.data.collectionByHandle.products.pageInfo.hasNextPage;
//         const edges = response.data.collectionByHandle.products.edges;

//         if (edges.length > 0) {
//           lastCursor = edges[edges.length - 1].cursor;

//           const tags = edges.map((edge) => edge.node.tags);
//           tagsFlat = tagsFlat.concat(tags.flat());
//         } else {
//           hasNextPage = false;
//         }
//       }

//       return tagsFlat;
//     } catch (error) {
//       console.error("Error getting collection tags: ", error);
//       return [];
//     }
//   }
//  async getAllFilteredTags() {
//   let tags = [];
//   const collectionHandle = window.location.pathname.split("/").pop();
//   const filtersFragment = this.buildTagFilterQuery(); // tags only
//   const applied = this._appliedFilters || [];

//   // helper: AND-match all applied tag filters against a product's tags
//   const matchesAllApplied = (productTags) =>
//     applied.length === 0 || applied.every((t) => productTags.includes(t));

//   if (
//     !this._hasFitment ||
//     !this._currentFitment ||
//     Object.keys(this._currentFitment).length === 0
//   ) {
//     const query = `query {
//       collectionByHandle(handle: "${collectionHandle}") {
//         products(first: 250${filtersFragment}) {
//           edges { node { tags } }
//         }
//       }
//     }`;

//     const response = await makeGraphQLRequest(query);
//     const edges = response.data.collectionByHandle.products.edges;

//     const filteredProducts = edges
//       .map((e) => e.node)
//       .filter((node) => matchesAllApplied(node.tags || []));

//     tags = filteredProducts.flatMap((n) => n.tags || []);
//   } else {
//     const query = `query {
//       collectionByHandle(handle: "${collectionHandle}") {
//         products(first: 250${filtersFragment}) {
//           edges {
//             node {
//               tags
//               variants(first: 1) {
//                 edges {
//                   node {
//                     metafield(namespace: "pim", key: "fitment_details") {
//                       value
//                       type
//                     }
//                   }
//                 }
//               }
//             }
//           }
//         }
//       }
//     }`;

//     const response = await makeGraphQLRequest(query);
//     const edges = response.data.collectionByHandle.products.edges;

//     for (const edge of edges) {
//       const product = edge.node;
//       const productTags = product.tags || [];

//       // fitment gate
//       const mf = product?.variants?.edges?.[0]?.node?.metafield;
//       const fits =
//         !mf?.value ? true : isSubmodelMatch(mf.value, this._currentFitment);

//       if (fits && matchesAllApplied(productTags)) {
//         tags = tags.concat(productTags);
//       }
//     }
//   }

//   return [...new Set(tags)];
// }

// buildTagFilterQuery() {
//   // ONLY encode applied tag filters; fitment is handled via metafield logic above
//   const appliedFilters = this._appliedFilters || [];
//   const tagFilters = appliedFilters
//     .filter(Boolean)
//     .map(
//       (filter) => `{ tag: "${String(filter).replace(/"/g, '\\"')}" }`
//     );

//   return tagFilters.length ? `, filters: [${tagFilters.join(", ")}]` : "";
// }



//   async updateCollectionTags(newTags) {
//     try {
//       let tags = [];
//       newTags.forEach((tag) => {
//         if (!tags.includes(tag)) {
//           tags.push(tag);
//         }
//       });
//       // Update the collection tags
//       this._collectionTags = tags;
//       await this.resetAndRebuildFilters(this._collectionTags);
//       this.toggleFilterLoading(false);
//     } catch (error) {
//       console.error("Error updating collection tags: ", error);
//     }
//   }

//   async applyFitmentToProductData(productDataHtml) {
//     console.log("Applying fitment to product data");
//     console.log("Product data:", productDataHtml);
//     console.log("Current fitment:", this._currentFitment);

//     const currentFitment = this._currentFitment;
//     if (!currentFitment || Object.keys(currentFitment).length === 0) {
//       console.log("No fitment data, skipping fitment application");
//       return;
//     }

//     const elements = productDataHtml.querySelectorAll(
//       "#main-collection-product-grid li > div"
//     );
//     console.log("Number of products to process:", elements.length);

//     let variantSkuList = [];

//     elements.forEach((element) => {
//       const variantObj = parseVariantData(element);
//       variantSkuList.push(variantObj);
//     });

//     for (let i = 0; i < variantSkuList.length; i++) {
//       const product = variantSkuList[i];
//       console.log("Processing product:", product);

//       const query = generateProductQuery(product.variantSku);
//       const response = await makeGraphQLRequest(query);

//       console.log("Product response:", response.data.products.edges);

//       const filteredProductData = this.filterProductByVariantSku(
//         response,
//         product.variantSku
//       );

//       let productMetafield;
//       // Check if the metafield exists at the product level or variant level
//       if (
//         filteredProductData.length > 0 &&
//         filteredProductData[0].node.metafield
//       ) {
//         productMetafield = filteredProductData[0].node.metafield.value;
//       } else if (
//         filteredProductData.length > 0 &&
//         filteredProductData[0].node.variants.edges[0].node.metafield
//       ) {
//         productMetafield =
//           filteredProductData[0].node.variants.edges[0].node.metafield.value;
//       }

//       // Handle the product metafield and tags
//       this.handleProductMetafieldAndTags(product, productMetafield, response);
//     }

//     console.log(
//       "Finished applying fitment. Products to remove:",
//       this._productsToRemove
//     );
//   }

//   filterProductByVariantSku(productsResponse, variantSku) {
//     return productsResponse.data.products.edges.filter(
//       (edge) => edge.node.variants.edges[0].node.sku === variantSku
//     );
//   }

//   handleProductMetafieldAndTags(product, productMetafield, response) {
//     console.log("Handling product metafield and tags for:", product);

//     let productsNotMatching = 0;
//     const currentFitment = this._currentFitment;
//     if (!currentFitment || Object.keys(currentFitment).length === 0) {
//       console.log("No fitment data, skipping product handling");
//       return productsNotMatching;
//     }

//     console.log("Product's metafield:", productMetafield);

//     const productMatched = productMetafield
//       ? isSubmodelMatch(productMetafield, currentFitment)
//       : null;

//     console.log("Product matched:", productMatched);

//     if (productMatched === false && productMatched !== null) {
//       productsNotMatching++;
//       this._productsToRemove.push(product.variantSku);
//       console.log(
//         "Product doesn't match fitment, added to remove list:",
//         product.variantSku
//       );
//     }

//     if (this._appliedFilters.length > 0) {
//       const resTags = response.data.products.edges.map(
//         (edge) => edge.node.tags
//       );
//       const resTagsFlat = resTags.flat();
//       resTagsFlat.forEach((tag) => {
//         if (!this._filterCollectionTags.includes(tag)) {
//           this._filterCollectionTags.push(tag);
//         }
//       });
//     }

//     return productsNotMatching;
//   }

//   removeNonFittingProduct(sku) {
//     const elements = document.querySelectorAll(
//       "#main-collection-product-grid li > div"
//     );

//     elements.forEach((element) => {
//       const variantObjSku = parseVariantData(element).variantSku;
//       if (variantObjSku === sku) {
//         element.parentElement.remove();
//       }
//     });
//     return;
//   }

//   toggleLoading(show) {
//     const loadingOverlay = document.querySelector(".loading-overlay--custom");
//     if (show) {
//       loadingOverlay.style.display = "block";
//     } else {
//       loadingOverlay.style.display = "none";
//     }
//   }

//   toggleFilterLoading(show) {
//     const loadingDiv = `<div id="js-loading-div" class="loading-overlay loading-overlay--custom" style="display: block;">
//                     <div class="loading-overlay__spinner" aria-label="{{ 'general.button_load_more.loading' | t }}"><svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>
//                 </div>`;
//     const tagsDiv = document.getElementById("tags");
//     if (show) {
//       tagsDiv.insertAdjacentHTML("afterbegin", loadingDiv);
//     }
//   }

//   toTitleCase(str) {
//     return str
//       .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // Insert space before each capital letter
//       .replace(/(\b[a-z](?!\s))/g, (x) => x.toUpperCase()); // Capitalize the first letter of each word
//   }

//   handleizeTag(input) {
//     return input
//       .toLowerCase() // Convert to lowercase
//       .replace(/:/g, "-") // Replace colons with hyphens
//       .replace(/\./g, "-") // Replace periods with hyphens
//       .replace(/[^a-z0-9-\s]/g, "") // Remove characters that are not alphanumeric, spaces, or hyphens
//       .replace(/\s+/g, "-") // Replace spaces with hyphens
//       .replace(/-+/g, "-") // Replace multiple hyphens with a single hyphen
//       .replace(/^-+|-+$/g, ""); // Remove leading and trailing hyphens
//   }

//   setLoadingState() {
//     this._sidebar.classList.add("loading");
//   }

//   saveFiltersToSession() {
//     const state = {
//       selectedPriceRange: this._selectedPriceRange,
//       appliedFilters: this._appliedFilters,
//       currentSortBy: this._currentSortBy.optionValue,
//     };
//     sessionStorage.setItem(
//       `fitlers-${window.location.pathname.split("/")[2]}`,
//       JSON.stringify(state)
//     );
//   }

//   loadFiltersFromSession() {
//     const savedState = sessionStorage.getItem(
//       `fitlers-${window.location.pathname.split("/")[2]}`
//     );

//     if (savedState) {
//       const state = JSON.parse(savedState);
//       this._selectedPriceRange = state.selectedPriceRange || { min: 0, max: 0 };
//       this._appliedFilters = state.appliedFilters || [];
//       this._currentSortBy.optionValue =
//         state.currentSortBy.optionValue || "default";

//       // Restore checkboxes based on the applied filters
//       this.setAppliedFacetFiltersChecked();

//       this.applyFilters();

//       // Show/Hide clear all filters button based on the applied filters
//       if (this._appliedFilters.length > 0) {
//         document.getElementById("clear-all-filters").style.display = "block";
//       } else {
//         document.getElementById("clear-all-filters").style.display = "none";
//       }
//     } else if (!savedState) {
//       this.saveFiltersToSession();
//       this.applyFilters();
//     }
//   }

//   setAppliedFacetFiltersChecked() {
//     this._appliedFilters.forEach((filterTag) => {
//       const [filterType, filterValue] = filterTag.split(":");
//       const checkboxSelector = `.filter-checkbox[name='${this.toTitleCase(
//         filterType
//       )}'][value='${filterValue}']`;
//       const checkboxElement = document.querySelector(checkboxSelector);
//       if (checkboxElement) {
//         checkboxElement.checked = true;
//       }
//     });
//   }

//   bindCheckboxEvents() {
//     document.querySelectorAll(".filter-checkbox").forEach((checkbox) => {
//       checkbox.addEventListener("change", (event) =>
//         this.handleFilterChange(event)
//       );
//     });
//   }
// }

// const collectionAttributeSidebar = new CollectionAttributeSidebar(
//   collectionTags
// );

// // Helper functions
// function sortBy(products, currentSortBy) {
//   switch (currentSortBy.optionValue) {
//     case "low-to-high":
//       return products.sort((a, b) => {
//         const priceA = parseFloat(
//           a
//             .querySelector(".price-item--regular")
//             .textContent.replace(/[^0-9.]/g, "")
//         );
//         const priceB = parseFloat(
//           b
//             .querySelector(".price-item--regular")
//             .textContent.replace(/[^0-9.]/g, "")
//         );
//         return priceA - priceB;
//       });
//     case "high-to-low":
//       return products.sort((a, b) => {
//         const priceA = parseFloat(
//           a
//             .querySelector(".price-item--regular")
//             .textContent.replace(/[^0-9.]/g, "")
//         );
//         const priceB = parseFloat(
//           b
//             .querySelector(".price-item--regular")
//             .textContent.replace(/[^0-9.]/g, "")
//         );
//         return priceB - priceA;
//       });
//     case "alpha-asc":
//       return products.sort((a, b) => {
//         const titleA = a.querySelector(".card-title").textContent.trim();
//         const titleB = b.querySelector(".card-title").textContent.trim();
//         return titleA.localeCompare(titleB);
//       });
//     case "alpha-desc":
//       return products.sort((a, b) => {
//         const titleA = a.querySelector(".card-title").textContent.trim();
//         const titleB = b.querySelector(".card-title").textContent.trim();
//         return titleB.localeCompare(titleA);
//       });
//     default:
//       return products;
//   }
// }

// function parseVariantData(element) {
//   const jsonData = element.getAttribute("data-json-product");
//   const decodedStr = decodeURIComponent(jsonData);
//   const parsedObj = JSON.parse(decodedStr);
//   return {
//     productTitle: parsedObj.variants[0].name,
//     variantSku: parsedObj.variants[0].sku,
//   };
// }

// function generateProductQuery(variantSku) {
//   return `query {
//             products(first:1, query: "${variantSku}") {
//               edges {
//                 node {
//                   title
//                   tags
//                   variants(first:1) {
//                     edges {
//                       node {
//                         metafield(namespace:"pim", key:"fitment_details") {
//                           value
//                           type
//                         }
//                       }
//                     }
//                   }
//                 }
//               }
//             }
//           }`;
// }

// async function makeGraphQLRequest(query) {
//   const response = await fetch(
//     "https://jks-manufacturing.myshopify.com/api/2025-04/graphql.json",
//     {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         "X-Shopify-Storefront-Access-Token": "cf6cb78c9ef033d960b53dd92195c129",
//       },
//       body: JSON.stringify({ query }),
//     }
//   );
//   const json = await response.json();
//   return json;
// }

// function isSubmodelMatch(fitmentStrings, fitmentSelection) {
//   const { year, make, model, submodel, drive_type, fuel_type } =
//     fitmentSelection;

//   function stringToObject(fitmentStrings) {
//     return fitmentStrings.split(";").map((str) => {
//       const [year, make, model, sub_model, drive_type, fuel_type] =
//         str.split("|");
//       return { year, make, model, sub_model, drive_type, fuel_type };
//     });
//   }

//   function isMatch(fitmentValue, inputValue, wildcard = "*") {
//     return (
//       inputValue &&
//       fitmentValue &&
//       (fitmentValue.toLowerCase() === inputValue.toLowerCase() ||
//         fitmentValue.toLowerCase() === wildcard)
//     );
//   }

//   const fitmentStringArray = stringToObject(fitmentStrings);
//   let isSubModelMatch = false;

//   for (const fitment of fitmentStringArray) {
//     const yearMatch = fitment.year === year;
//     const makeMatch = isMatch(fitment.make, make);
//     const modelMatch = isMatch(fitment.model, model);
//     const driveTypeMatch = isMatch(fitment.drive_type, drive_type);
//     const fuelTypeMatch = isMatch(fitment.fuel_type, fuel_type);
//     const submodelMatch = isMatch(fitment.sub_model, submodel);

//     // Check if all fields match (considering 'X' as wildcard for submodel)
//     if (
//       yearMatch &&
//       makeMatch &&
//       modelMatch &&
//       (driveTypeMatch || driveTypeMatch === undefined) &&
//       (fuelTypeMatch || fuelTypeMatch === undefined) &&
//       (submodelMatch || submodelMatch === undefined)
//     ) {
//       isSubModelMatch = true;
//       break;
//     }
//   }

//   return isSubModelMatch;
// }

// // Open sidebar externally
// function toggleFilterSidebar() {
//   const sidebar = document.getElementById("collection-attribute-sidebar");
//   if (sidebar.classList.contains("filter-sidebar-open")) {
//     sidebar.classList.remove("filter-sidebar-open");
//   } else {
//     sidebar.classList.add("filter-sidebar-open");
//   }
// }
